/**
 * Permission- and feature-aware rendering.
 *
 * These are UX affordances, not security. Every one has a server-side counterpart in
 * `apps.rbac.permissions.HasPermission` and `apps.feature_flags.permissions.
 * FeatureEnabled`; hiding a button never substitutes for the API check, and a reviewer
 * finding a capability guarded only here should treat it as a defect.
 */

import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { useAuthStore } from '@/stores/auth-store';
import type { FlagState } from '@/types/api';

/* -------------------------------------------------------------------------- */
/* Hooks                                                                      */
/* -------------------------------------------------------------------------- */

export function usePermission(permission: string): boolean {
  return useAuthStore((state) => state.can(permission));
}

export function useAnyPermission(permissions: string[]): boolean {
  return useAuthStore((state) => state.canAny(permissions));
}

export function useFeature(flag: string): FlagState {
  return useAuthStore((state) => state.featureState(flag));
}

/**
 * True for a platform operator — the deployment's own staff, who administer every
 * organisation rather than one.
 *
 * This is a flag on the user record, not a tenant permission, because platform
 * administration crosses tenant boundaries. The server enforces the same flag on every
 * cross-tenant endpoint (`apps.tenants.permissions.IsPlatformAdmin`); hiding a menu entry
 * here never substitutes for that check.
 */
export function usePlatformAdmin(): boolean {
  return useAuthStore((state) => Boolean(state.user?.is_platform_admin));
}

/** True when the module permits writes. `read_only` deliberately returns false. */
export function useFeatureWritable(flag: string): boolean {
  return useFeature(flag) === 'enabled';
}

/* -------------------------------------------------------------------------- */
/* Components                                                                 */
/* -------------------------------------------------------------------------- */

interface CanProps {
  /** A single permission code. */
  permission?: string;
  /** Any one of these codes is sufficient. */
  anyOf?: string[];
  children: ReactNode;
  /** Rendered instead when the check fails. Defaults to nothing. */
  fallback?: ReactNode;
}

/**
 * Render children only if the caller holds the permission.
 *
 *   <Can permission="role.create">
 *     <Button>New role</Button>
 *   </Can>
 */
export function Can({ permission, anyOf, children, fallback = null }: CanProps) {
  const single = useAuthStore((state) => (permission ? state.can(permission) : true));
  const any = useAuthStore((state) => (anyOf?.length ? state.canAny(anyOf) : true));

  return <>{single && any ? children : fallback}</>;
}

interface FeatureProps {
  flag: string;
  children: ReactNode;
  /** Also render when the module is read-only. Default true — see below. */
  includeReadOnly?: boolean;
  fallback?: ReactNode;
}

/**
 * Render children according to a module's state.
 *
 * Read-only renders by default: a user should see that a capability exists and is
 * currently locked by policy, rather than watch it vanish with no explanation. Controls
 * inside are expected to disable themselves via `useFeatureWritable`.
 */
export function Feature({
  flag,
  children,
  includeReadOnly = true,
  fallback = null,
}: FeatureProps) {
  const state = useFeature(flag);
  const visible = state === 'enabled' || (includeReadOnly && state === 'read_only');

  return <>{visible ? children : fallback}</>;
}

/* -------------------------------------------------------------------------- */
/* Route guards                                                               */
/* -------------------------------------------------------------------------- */

/** Redirects to sign-in, remembering where the user was going. */
export function RequireAuth() {
  const user = useAuthStore((state) => state.user);
  const isRestoring = useAuthStore((state) => state.isRestoring);
  const location = useLocation();

  if (isRestoring) return <RouteSpinner />;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;

  return <Outlet />;
}

interface RequirePermissionProps {
  permission?: string;
  anyOf?: string[];
  flag?: string;
}

/** Guards a route subtree behind a permission and, optionally, a module flag. */
export function RequirePermission({ permission, anyOf, flag }: RequirePermissionProps) {
  const canSingle = useAuthStore((state) => (permission ? state.can(permission) : true));
  const canAny = useAuthStore((state) => (anyOf?.length ? state.canAny(anyOf) : true));
  const featureState = useAuthStore((state) =>
    flag ? state.featureState(flag) : ('enabled' as FlagState),
  );

  if (!canSingle || !canAny || featureState === 'disabled') {
    return <Navigate to="/forbidden" replace />;
  }

  return <Outlet />;
}

/** Guards a route subtree behind platform-operator status. */
export function RequirePlatformAdmin() {
  const isPlatformAdmin = useAuthStore((state) => Boolean(state.user?.is_platform_admin));

  if (!isPlatformAdmin) return <Navigate to="/forbidden" replace />;

  return <Outlet />;
}

function RouteSpinner() {
  return (
    <div className="flex min-h-[50vh] items-center justify-center" role="status" aria-live="polite">
      <span className="h-6 w-6 animate-spin rounded-full border-2 border-muted border-t-primary" />
      <span className="sr-only">Loading</span>
    </div>
  );
}
